// 認証関連のカスタムフック

import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useCallback, useEffect, useState } from 'react';
import { isUserInWorkspace } from '@/lib/auth/domain-validation';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthorized: boolean; // ワークスペースアクセス権限
  user: {
    id?: string;
    name?: string | null;
    email?: string | null;
    image?: string | null;
  } | null;
  accessToken?: string;
  idToken?: string;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * 環境がローカルかどうかを判定する関数
 */
function isLocalEnvironment(): boolean {
  return import.meta.env.VITE_APP_ENV === 'local';
}

/**
 * 認証状態とトークン情報を取得するフック
 */
export function useAuth(): AuthState {
  const auth = useOidcAuth();
  const user = auth.user;
  const [isAuthorized, setIsAuthorized] = useState(false);

  // ローカル環境の場合は認証をスキップして成功させる
  if (isLocalEnvironment()) {
    return {
      isAuthenticated: true,
      isLoading: false,
      isAuthorized: true,
      user: {
        id: 'local-user',
        name: 'Local Development User',
        email: 'local@full-kaiten.com',
        image: null,
      },
      accessToken: undefined,
      idToken: undefined,
      signIn: async () => {},
      signOut: async () => {},
    };
  }

  const isLoading = auth.isLoading;
  const isAuthenticated = auth.isAuthenticated && !!user;

  // ワークスペース認可チェック
  useEffect(() => {
    if (isAuthenticated && user?.profile) {
      const userProfile = {
        email: user.profile.email || user.profile.preferred_username || null,
        name: user.profile.name || user.profile.preferred_username || null,
      };
      const authorized = isUserInWorkspace(userProfile);
      setIsAuthorized(authorized);

      if (!authorized) {
        console.warn('❌ ユーザーがワークスペースメンバーではありません:', userProfile.email);
      }
    } else {
      setIsAuthorized(false);
    }
  }, [isAuthenticated, user]);

  const userProfile = user?.profile
    ? {
        id: user.profile.sub,
        name: user.profile.name || user.profile.preferred_username || null,
        email: user.profile.email || user.profile.preferred_username || null,
        image: user.profile.picture || null,
      }
    : null;

  return {
    isAuthenticated,
    isLoading,
    isAuthorized,
    user: userProfile,
    accessToken: user?.access_token,
    idToken: user?.id_token,
    signIn: () => auth.signinRedirect(),
    signOut: () => auth.removeUser(),
  };
}

/**
 * APIリクエスト用のAuthorizationヘッダーを取得するフック
 */
export function useAuthHeader() {
  const auth = useOidcAuth();
  const user = auth.user;

  const isLoading = auth.isLoading;
  const isAuthenticated = auth.isAuthenticated && !!user;

  const getAuthHeader = useCallback((): Record<string, string> => {
    // ローカル環境の場合は空のBearerトークンを返す
    if (isLocalEnvironment()) {
      return {
        Authorization: 'Bearer ',
        'x-user-email': 'local@full-kaiten.com',
      };
    }

    // 認証状態がまだ準備中の場合は空のヘッダーを返す
    if (isLoading) {
      return {};
    }

    // 認証されていない場合は空のヘッダーを返す
    if (!isAuthenticated || !user?.profile) {
      console.warn('❌ 認証されていないため、認証ヘッダーを提供しません');
      return {};
    }

    // 毎回直接ワークスペース認可チェックを実行（状態に依存しない）
    const userProfile = {
      email: user.profile.email || user.profile.preferred_username || null,
      name: user.profile.name || user.profile.preferred_username || null,
    };
    const isCurrentlyAuthorized = isUserInWorkspace(userProfile);

    // ワークスペース認可がない場合は空のヘッダーを返す
    if (!isCurrentlyAuthorized) {
      console.warn('❌ ワークスペース認可がないため、認証ヘッダーを提供しません', {
        isCurrentlyAuthorized,
        isAuthenticated,
        userEmail: userProfile.email,
      });
      return {};
    }

    // JWT Authorizerのaudience設定問題の回避: IDトークンを優先使用（一時的対応）
    // IDトークンにはaudienceフィールドが含まれているため、現在のインフラ設定で動作する
    const token = user?.id_token || user?.access_token;

    if (token) {
      // 期限切れチェックはProtectedRouteに任せ、ここでは単純にトークンを返す
      return {
        Authorization: `Bearer ${token}`,
        // 直前の isUserInWorkspace が email 非 null を保証するため '' には到達しない
        // （string | null を string へ narrowing するための型合わせのみ）
        'x-user-email': userProfile.email ?? '',
      };
    }

    console.error('❌ トークンが見つからないため、空のヘッダーを返します:', {
      hasIdToken: !!user?.id_token,
      hasAccessToken: !!user?.access_token,
      user: user,
    });
    return {};
  }, [user, isAuthenticated, isLoading]);

  // ローカル環境の場合は認可状態をtrueにする
  const userProfile = user?.profile
    ? {
        email: user.profile.email || user.profile.preferred_username || null,
        name: user.profile.name || user.profile.preferred_username || null,
      }
    : null;
  const isAuthorized = isLocalEnvironment()
    ? true
    : isAuthenticated && userProfile
      ? isUserInWorkspace(userProfile)
      : false;

  return {
    getAuthHeader,
    isAuthorized,
    isLoading,
    isAuthenticated: isLocalEnvironment() ? true : isAuthenticated,
  };
}
